import { useState, useEffect, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Users, User, Plus, Search, Trash2, Edit, X, PauseCircle, PlayCircle, UserX, UserCheck } from 'lucide-react';
import { useCompany } from '../context/CompanyContext';
import { cn } from '../utils/formatters';
import { btnGhost, input, label, modalOverlay, modalContent, modalHeader, modalBody, modalFooter, formGroup } from '../styles/components';
import type { User as UserModel, UserStatus } from '../types';

const USERS_KEY = 'reformall_users';

const statusOf = (u: UserModel): UserStatus => u.status ?? 'active';

const STATUS_META: Record<UserStatus, { label: string; pill: string }> = {
  active: { label: 'Activo', pill: 'b2b-pill--ok' },
  suspended: { label: 'En pausa', pill: 'b2b-pill--warn' },
  inactive: { label: 'De baja', pill: 'b2b-pill--danger' },
};

type UserFormState = {
  name: string;
  email: string;
  role: UserModel['role'];
  companyId: string;
};

const emptyForm: UserFormState = { name: '', email: '', role: 'admin', companyId: '' };

export function SuperAdminUsersPage() {
  const { isLoading, companies } = useCompany();
  const [users, setUsers] = useState<UserModel[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserModel | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState<UserFormState>(emptyForm);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(USERS_KEY);
      setUsers(stored ? JSON.parse(stored) : []);
    } catch {
      setUsers([]);
    } finally {
      setIsLoadingUsers(false);
    }
  }, []);

  const filteredUsers = users.filter(u =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  const persist = (list: UserModel[]) => {
    localStorage.setItem(USERS_KEY, JSON.stringify(list));
    setUsers(list);
  };

  const handleDeleteUser = async (id: string) => {
    if (!confirm('¿Eliminar definitivamente este usuario? Esta acción no se puede deshacer.')) return;
    persist(users.filter(u => u.id !== id));
  };

  const setStatus = (u: UserModel, status: UserStatus) => {
    const action = status === 'active' ? 'reactivar' : `poner como "${STATUS_META[status].label.toLowerCase()}"`;
    if (!confirm(`¿Seguro que quieres ${action} a ${u.name} (${u.email})?`)) return;
    persist(users.map(m => m.id === u.id
      ? { ...m, status, updatedAt: new Date().toISOString() }
      : m));
  };

  const openCreate = () => {
    setEditingUser(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (user: UserModel) => {
    setEditingUser(user);
    setForm({ name: user.name, email: user.email, role: user.role, companyId: user.companyId || '' });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setForm(emptyForm);
  };

  const handleSaveUser = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (editingUser) {
        persist(users.map(u => u.id === editingUser.id
          ? { ...u, name: form.name, email: form.email, role: form.role, companyId: form.companyId || null, updatedAt: new Date().toISOString() }
          : u));
      } else {
        const now = new Date().toISOString();
        persist([...users, {
          id: crypto.randomUUID(),
          name: form.name,
          email: form.email,
          role: form.role,
          companyId: form.companyId || null,
          emailVerified: true,
          status: 'active',
          createdAt: now,
          updatedAt: now,
        }]);
      }
      closeModal();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading || isLoadingUsers) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="text-center">
          <Users className="w-12 h-12 mx-auto mb-4 opacity-40 animate-spin" />
          <p className="b2b-muted">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div className="space-y-1">
          <span className="b2b-eyebrow">SuperAdmin</span>
          <h1 className="text-3xl font-extrabold tracking-tight">Mantenimiento de Usuarios</h1>
          <p className="b2b-muted">Gestión de todos los usuarios del sistema</p>
        </div>
        <div className="flex gap-2">
          <button onClick={openCreate} className="b2b-btn b2b-btn--primary">
            <Plus className="w-4 h-4" /> Nuevo usuario
          </button>
          <Link to="/superadmin" className="b2b-btn b2b-btn--ghost">
            <X className="w-4 h-4" /> Volver
          </Link>
        </div>
      </div>

      <div className="b2b-panel">
        <div className="b2b-panel__head">
          <h2 className="text-lg font-semibold">Lista de usuarios</h2>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 b2b-muted" />
            <input
              type="text"
              placeholder="Buscar por nombre o email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className={cn(input, 'pl-9 w-64')}
            />
          </div>
        </div>

        {filteredUsers.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <h3 className="text-lg font-semibold">No hay usuarios</h3>
          </div>
        ) : (
          filteredUsers.map(u => {
            const status = statusOf(u);
            const meta = STATUS_META[status];
            return (
              <div key={u.id} className="b2b-panel__row">
                <span className="b2b-icon">
                  {u.avatar ? (
                    <img src={u.avatar} alt="" className="w-full h-full object-cover rounded-full" />
                  ) : (
                    <User className="w-5 h-5" />
                  )}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block font-medium truncate">{u.name}</span>
                  <span className="block text-sm b2b-muted truncate">{u.email}</span>
                </span>
                <span className={cn('b2b-pill', meta.pill)}>{meta.label}</span>
                <span className="b2b-pill">{u.role}</span>
                {u.companyId && (
                  <span className="text-xs b2b-muted hidden md:inline">Empresa: {u.companyId.substring(0, 8)}…</span>
                )}
                {u.role !== 'superadmin' && (
                  <span className="flex items-center gap-1">
                    {status === 'active' && (
                      <button onClick={() => setStatus(u, 'suspended')} className="p-2 rounded-lg hover:bg-[var(--b2b-surface-muted)]" aria-label="Pausar usuario" title="Pausar">
                        <PauseCircle className="w-4 h-4" />
                      </button>
                    )}
                    {status === 'suspended' && (
                      <button onClick={() => setStatus(u, 'active')} className="p-2 rounded-lg hover:bg-[var(--b2b-surface-muted)]" aria-label="Reanudar usuario" title="Reanudar">
                        <PlayCircle className="w-4 h-4" />
                      </button>
                    )}
                    {status !== 'inactive' && (
                      <button onClick={() => setStatus(u, 'inactive')} className="p-2 rounded-lg hover:bg-[var(--b2b-surface-muted)]" aria-label="Dar de baja" title="Dar de baja">
                        <UserX className="w-4 h-4" />
                      </button>
                    )}
                    {status === 'inactive' && (
                      <button onClick={() => setStatus(u, 'active')} className="p-2 rounded-lg hover:bg-[var(--b2b-surface-muted)]" aria-label="Reactivar" title="Reactivar">
                        <UserCheck className="w-4 h-4" />
                      </button>
                    )}
                    <button onClick={() => openEdit(u)} className="p-2 rounded-lg hover:bg-[var(--b2b-surface-muted)]" aria-label="Editar usuario">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDeleteUser(u.id)} className="p-2 rounded-lg hover:bg-[var(--b2b-surface-muted)] text-[color:var(--b2b-danger)]" aria-label="Eliminar usuario">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>

      {showModal && (
        <div className={modalOverlay} onClick={closeModal} role="dialog" aria-modal="true" aria-labelledby="user-modal-title">
          <div className={cn(modalContent, 'w-full max-w-lg')} onClick={e => e.stopPropagation()}>
            <div className={modalHeader}>
              <h2 id="user-modal-title" className="text-lg font-semibold">
                {editingUser ? 'Editar usuario' : 'Nuevo usuario'}
              </h2>
              <button onClick={closeModal} className="p-2 rounded-lg hover:bg-[var(--b2b-surface-muted)]" aria-label="Cerrar">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form id="user-form" onSubmit={handleSaveUser} className={cn(modalBody, 'space-y-4')} noValidate>
              <div className={formGroup}>
                <label htmlFor="name" className={label}>Nombre completo *</label>
                <input id="name" type="text" className={input} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
              </div>

              <div className={formGroup}>
                <label htmlFor="email" className={label}>Email *</label>
                <input id="email" type="email" className={input} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
              </div>

              <div className={formGroup}>
                <label htmlFor="role" className={label}>Rol</label>
                <select id="role" className={input} value={form.role} onChange={e => setForm({ ...form, role: e.target.value as UserModel['role'] })}>
                  <option value="superadmin">SuperAdmin</option>
                  <option value="admin">Admin de Empresa</option>
                  <option value="worker">Operario/Albañil</option>
                </select>
              </div>

              <div className={formGroup}>
                <label htmlFor="companyId" className={label}>Empresa asociada (solo admin/worker)</label>
                <select id="companyId" className={input} value={form.companyId} onChange={e => setForm({ ...form, companyId: e.target.value })}>
                  <option value="">Ninguna (SuperAdmin)</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </form>
            <div className={modalFooter}>
              <button type="button" onClick={closeModal} className={btnGhost}>Cancelar</button>
              <button type="submit" form="user-form" className="b2b-btn b2b-btn--primary disabled:opacity-50" disabled={isSubmitting}>
                {isSubmitting ? 'Guardando...' : (editingUser ? 'Actualizar' : 'Crear')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
